import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

const ALERT_HISTORY_SETTINGS_KEY = "alertHistorySettings";

const DEFAULT_SETTINGS = {
  defaultPagination: 10,
  defaultView: "grid",
  defaultSorting: "timestamp",
};

export function AlertHistorySettings({ open, onClose, onSave }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);

  // Load settings from localStorage on open
  useEffect(() => {
    if (open) {
      const savedSettings = localStorage.getItem(ALERT_HISTORY_SETTINGS_KEY);
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setSettings({ ...DEFAULT_SETTINGS, ...parsed });
        } catch (e) {
          console.error("Error parsing saved settings:", e);
          setSettings(DEFAULT_SETTINGS);
        }
      } else {
        setSettings(DEFAULT_SETTINGS);
      }
      setHasChanges(false);
    }
  }, [open]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Save to localStorage
    localStorage.setItem(ALERT_HISTORY_SETTINGS_KEY, JSON.stringify(settings));

    // Notify parent component to apply settings
    if (onSave) {
      onSave(settings);
    }

    setHasChanges(false);
    onClose();
  };

  const handleCancel = () => {
    // Reset to saved settings
    const savedSettings = localStorage.getItem(ALERT_HISTORY_SETTINGS_KEY);
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      } catch (e) {
        setSettings(DEFAULT_SETTINGS);
      }
    } else {
      setSettings(DEFAULT_SETTINGS);
    }
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon sx={{ color: "rgb(255, 133, 85)" }} />
          <Typography
            sx={{
              lineHeight: "1.6",
              letterSpacing: "0.0075em",
              flex: "0 0 auto",
              fontFamily: "Roboto, sans-serif",
              fontSize: "20px",
              color: "rgb(255, 133, 85)",
              fontWeight: 600,
            }}
          >
            Alert History Settings
          </Typography>
        </Box>
        <IconButton
          onClick={handleCancel}
          size="small"
          aria-label="Close"
          sx={{ color: "#4A5565" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Default Pagination Count */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <Select
              value={settings.defaultPagination}
              onChange={(e) =>
                handleChange("defaultPagination", e.target.value)
              }
              sx={{
                marginTop: "10px",
                fontFamily: "Arial, sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "1.4375em",
                color: "rgb(33, 33, 33)",
                boxSizing: "border-box",
                cursor: "text",
                display: "inline-flex",
                alignItems: "center",
                width: "100%",
                position: "relative",
                borderRadius: "10px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#bdbdbd",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
                "& .MuiSelect-select": {
                  fontFamily: "Arial, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "1.4375em",
                  color: "rgb(33, 33, 33)",
                  boxSizing: "border-box",
                },
              }}
            >
              <MenuItem value={10} sx={{ fontFamily: "Roboto, sans-serif" }}>
                10
              </MenuItem>
              <MenuItem value={25} sx={{ fontFamily: "Roboto, sans-serif" }}>
                25
              </MenuItem>
              <MenuItem value={50} sx={{ fontFamily: "Roboto, sans-serif" }}>
                50
              </MenuItem>
            </Select>
            <Typography
              sx={{
                margin: "8px 0px 0px",
                fontWeight: 400,
                lineHeight: 1.5,
                color: "rgb(74, 85, 101)",
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
              }}
            >
              Default Count Pagination
            </Typography>
          </FormControl>
        </Box>

        {/* <Divider sx={{ my: 2 }} /> */}

        {/* Default View */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <Select
              value={settings.defaultView}
              onChange={(e) => handleChange("defaultView", e.target.value)}
              sx={{
                fontFamily: "Arial, sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "1.4375em",
                color: "rgb(33, 33, 33)",
                boxSizing: "border-box",
                cursor: "text",
                display: "inline-flex",
                alignItems: "center",
                width: "100%",
                position: "relative",
                borderRadius: "10px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#bdbdbd",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
                "& .MuiSelect-select": {
                  fontFamily: "Arial, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "1.4375em",
                  color: "rgb(33, 33, 33)",
                  boxSizing: "border-box",
                },
              }}
            >
              <MenuItem value="grid" sx={{ fontFamily: "Roboto, sans-serif" }}>
                Grid
              </MenuItem>
              <MenuItem value="list" sx={{ fontFamily: "Roboto, sans-serif" }}>
                List
              </MenuItem>
            </Select>
            <Typography
              sx={{
                margin: "8px 0px 0px",
                fontWeight: 400,
                lineHeight: 1.5,
                color: "rgb(74, 85, 101)",
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
              }}
            >
              Default View
            </Typography>
          </FormControl>
        </Box>

        {/* <Divider sx={{ my: 2 }} /> */}

        {/* Default Sorting */}
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth>
            <Select
              value={settings.defaultSorting}
              onChange={(e) => handleChange("defaultSorting", e.target.value)}
              sx={{
                fontFamily: "Arial, sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "1.4375em",
                color: "rgb(33, 33, 33)",
                boxSizing: "border-box",
                cursor: "text",
                display: "inline-flex",
                alignItems: "center",
                width: "100%",
                position: "relative",
                borderRadius: "10px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#bdbdbd",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
                "& .MuiSelect-select": {
                  fontFamily: "Arial, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "1.4375em",
                  color: "rgb(33, 33, 33)",
                  boxSizing: "border-box",
                },
              }}
            >
              <MenuItem
                value="alertName"
                sx={{ fontFamily: "Roboto, sans-serif" }}
              >
                By Name
              </MenuItem>
              <MenuItem
                value="timestamp"
                sx={{ fontFamily: "Roboto, sans-serif" }}
              >
                By Date
              </MenuItem>
              <MenuItem
                value="sourceName"
                sx={{ fontFamily: "Roboto, sans-serif" }}
              >
                By Source
              </MenuItem>
              <MenuItem
                value="status"
                sx={{ fontFamily: "Roboto, sans-serif" }}
              >
                By Status
              </MenuItem>
            </Select>
            <Typography
              sx={{
                margin: "8px 0px 0px",
                fontWeight: 400,
                lineHeight: 1.5,
                color: "rgb(74, 85, 101)",
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
              }}
            >
              Default Sorting
            </Typography>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          //   py: 2,
          pb: 2,
          pt: 0,
          //   borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button
          onClick={handleCancel}
          sx={{
            fontFamily: "Roboto, sans-serif",
            textTransform: "none",
            color: "#4A5565",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            fontFamily: "Roboto, sans-serif",
            textTransform: "none",
            borderRadius: "10px",
            backgroundColor: "#2932E5",
            "&:hover": {
              backgroundColor: "#1f28c7",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
